import React, { useMemo, useEffect, useState, useRef } from 'react';
import { NewNGJob } from '../types';
import { newNGApi } from '../services/newNGApi';
import Typography from '../../../basic/Typography';
import styles from '../NewNG.module.css';

interface TheatreSchedulePanelProps {
  jobs: NewNGJob[];
  currentJob: NewNGJob | null;
  maxUpcomingItems?: number;
}

interface EnhancedJobInfo {
  [jobId: string]: any;
}

export const TheatreSchedulePanel: React.FC<TheatreSchedulePanelProps> = ({
  jobs,
  currentJob,
  maxUpcomingItems = 5
}) => {
  const [enhancedInfo, setEnhancedInfo] = useState<EnhancedJobInfo>({});
  const [rotatingIndex, setRotatingIndex] = useState(0);
  const loadedJobsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const loadEnhancedInfo = async () => {
      const relevantJobs = [...jobs.filter(j =>
        j.overall_status === 'in_progress' ||
        j.overall_status === 'processing' ||
        j.job_id === currentJob?.job_id
      )];

      const completedJobs = jobs.filter(job =>
        job.overall_status === 'completed' &&
        job.stages?.composition?.completed === true
      ).slice(0, maxUpcomingItems + 1);

      relevantJobs.push(...completedJobs);

      const uniqueJobs = Array.from(new Set(relevantJobs.map(j => j.job_id)))
        .map(id => relevantJobs.find(j => j.job_id === id)!);

      for (const job of uniqueJobs) {
        if (!loadedJobsRef.current.has(job.job_id)) {
          try {
            const info = await newNGApi.getEnhancedJobInfo(job.job_id);
            setEnhancedInfo(prev => ({ ...prev, [job.job_id]: info }));
            loadedJobsRef.current.add(job.job_id);
          } catch (error) {
            console.warn(`Could not load enhanced info for ${job.job_id}`);
          }
        }
      }
    };

    loadEnhancedInfo();
  }, [jobs, currentJob, maxUpcomingItems]);

  const scheduleData = useMemo(() => {
    const inProgressJobs = jobs.filter(job =>
      job.overall_status === 'in_progress' ||
      job.overall_status === 'processing'
    );

    const completedJobs = jobs.filter(job =>
      job.overall_status === 'completed' &&
      job.stages?.composition?.completed === true
    );

    const sortedCompleted = completedJobs
      .filter(job => job.created_at)
      .sort((a, b) => {
        const dateA = new Date(a.created_at || '').getTime();
        const dateB = new Date(b.created_at || '').getTime();
        return dateB - dateA;
      });

    const upcomingJobs = sortedCompleted
      .filter(job => job.job_id !== currentJob?.job_id)
      .slice(0, maxUpcomingItems);

    return {
      inProgress: inProgressJobs[0] || null,
      currentlyPlaying: currentJob,
      upcoming: upcomingJobs
    };
  }, [jobs, currentJob, maxUpcomingItems]);

  useEffect(() => {
    if (scheduleData.upcoming.length > 1) {
      const interval = setInterval(() => {
        setRotatingIndex(prev => (prev + 1) % scheduleData.upcoming.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [scheduleData.upcoming.length]);

  const getJobTitle = (job: NewNGJob): string => {
    const info = enhancedInfo[job.job_id];

    if (info?.script?.show_metadata?.title) {
      return info.script.show_metadata.title;
    }

    if (info?.metadata?.query?.topic) {
      return info.metadata.query.topic;
    }

    if (job.title) {
      return job.title;
    }

    return `News ${job.job_id.slice(0, 8)}`;
  };

  const hasInProgress = scheduleData.inProgress !== null;
  const hasUpcoming = scheduleData.upcoming.length > 0;

  if (!scheduleData.currentlyPlaying && !hasInProgress && !hasUpcoming) {
    return null;
  }

  let playOrder = 1;

  return (
    <div className={styles['newng-theatre-schedule-panel']}>
      <div className={styles['newng-theatre-schedule-header']}>
        <Typography variant="news-logo" component="div">
          Coming Up
        </Typography>
      </div>

      <div className={styles['newng-theatre-schedule-content']}>
        {hasInProgress && (
          <div className={styles['newng-theatre-schedule-item']}>
            <div className={styles['newng-theatre-schedule-number']}>
              <Typography variant="body1" component="span">{playOrder++}</Typography>
            </div>
            <div className={styles['newng-theatre-schedule-info']}>
              <div className={styles['newng-theatre-schedule-status-tag']}>
                <Typography variant="caption" component="span">Rendering</Typography>
              </div>
              <div className={styles['newng-theatre-schedule-title']}>
                <Typography variant="body1" component="div">
                  {getJobTitle(scheduleData.inProgress!)}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {scheduleData.currentlyPlaying && (
          <div className={`${styles['newng-theatre-schedule-item']} ${styles['newng-theatre-schedule-item-playing']}`}>
            <div className={styles['newng-theatre-schedule-number']}>
              <Typography variant="body1" component="span">{playOrder++}</Typography>
            </div>
            <div className={styles['newng-theatre-schedule-info']}>
              <div className={`${styles['newng-theatre-schedule-status-tag']} ${styles['newng-theatre-schedule-status-tag-playing']}`}>
                <Typography variant="caption" component="span">Playing</Typography>
              </div>
              <div className={styles['newng-theatre-schedule-title']}>
                <Typography variant="body1" component="div">
                  {getJobTitle(scheduleData.currentlyPlaying)}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {hasUpcoming && scheduleData.upcoming[rotatingIndex] && (
          <div className={`${styles['newng-theatre-schedule-item']} ${styles['newng-theatre-schedule-item-rotating']}`} key={scheduleData.upcoming[rotatingIndex].job_id}>
            <div className={styles['newng-theatre-schedule-number']}>
              <Typography variant="body1" component="span">{playOrder + rotatingIndex}</Typography>
            </div>
            <div className={styles['newng-theatre-schedule-info']}>
              <div className={styles['newng-theatre-schedule-title']}>
                <Typography variant="body1" component="div">
                  {getJobTitle(scheduleData.upcoming[rotatingIndex])}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
